import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useCurrency } from "../context/CurrencyContext";
import {
  Building2, Users, Clock, CheckCircle2, XCircle, UserPlus, Trash2, Loader2, Mail, Upload, FileText, Download, FileDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessDashboard() {
  const nav = useNavigate();
  const { format } = useCurrency();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("requests");
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");
  const [deciding, setDeciding] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const meRes = await axios.get(`${API}/business/me`);
      setMe(meRes.data);
      if (!meRes.data?.company) return;
      if (meRes.data.role === "admin") {
        const m = await axios.get(`${API}/business/members`);
        setMembers(m.data.members || []);
      }
      const r = await axios.get(`${API}/business/booking-requests`);
      setRequests(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  }, []);
  // Soft refresh — used after inline actions (invite, decision, bulk import) so
  // BulkImportPanel's per-row result summary isn't wiped by the top-level
  // `loading` spinner unmounting the whole tab tree.
  const softRefresh = useCallback(async () => {
    try {
      const meRes = await axios.get(`${API}/business/me`);
      setMe(meRes.data);
      if (!meRes.data?.company) return;
      if (meRes.data.role === "admin") {
        const m = await axios.get(`${API}/business/members`);
        setMembers(m.data.members || []);
      }
      const r = await axios.get(`${API}/business/booking-requests`);
      setRequests(r.data.items || []);
    } catch { /* silent — non-blocking refresh */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return toast.error("Email is required");
    try {
      setInviting(true);
      const { data } = await axios.post(`${API}/business/invites`, {
        email: inviteEmail.trim().toLowerCase(), name: inviteName.trim(), role: inviteRole,
      });
      toast.success(data.status === "added" ? `${data.email} added to your team` : `Invite sent to ${data.email}`);
      setInviteEmail(""); setInviteName(""); setInviteRole("employee");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally { setInviting(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email} from ${me.company.name}?`)) return;
    try {
      await axios.delete(`${API}/business/members/${m.user_id}`);
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  const decide = async (req, action) => {
    try {
      const { data } = await axios.post(`${API}/business/booking-requests/${req.id}/${action}`, { reason });
      toast.success(action === "approve" ? "Approved — booking confirmed" : "Request declined");
      setDeciding(null); setReason("");
      await load();
      if (action === "approve" && data.booking_id) {
        // Optional deep link to the created booking.
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    }
  };

  if (loading) return <div className="mx-auto max-w-md px-6 py-16 text-center text-slate-500"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  if (!me?.company) {
    return (
      <div className="mx-auto max-w-lg px-6 py-16 text-center" data-testid="no-company-cta">
        <Building2 className="mx-auto h-12 w-12 text-copper" />
        <h1 className="mt-4 font-display text-2xl font-bold">Register your company on EasyStay Business</h1>
        <p className="mt-2 text-slate-600">Book staff accommodation without your employees paying personally.</p>
        <Link to="/business/register" className="btn-primary mt-6 inline-block text-sm">Get started</Link>
      </div>
    );
  }

  const isAdmin = me.role === "admin";
  const pending = requests.filter((r) => r.status === "pending");

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8" data-testid="business-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
            <Building2 className="h-4 w-4" /> EasyStay Business
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">{me.company.name}</h1>
          <div className="mt-1 text-sm text-slate-500">
            {me.company.member_count} member{me.company.member_count === 1 ? "" : "s"} · Billing: {me.company.billing_email} · You are {isAdmin ? "the company admin" : "an employee"}
          </div>
        </div>
        {!isAdmin && (
          <button onClick={() => nav("/search")} data-testid="employee-book-cta" className="btn-primary text-sm">
            Request accommodation →
          </button>
        )}
      </div>

      <div className="mt-8 flex gap-2 border-b border-slate-200">
        <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} testId="tab-requests">
          Booking requests · {pending.length}
        </TabBtn>
        {isAdmin && (
          <TabBtn active={tab === "team"} onClick={() => setTab("team")} testId="tab-team">
            Team · {members.length}
          </TabBtn>
        )}
      </div>

      {tab === "requests" && (
        <div className="mt-6 space-y-3" data-testid="requests-list">
          {!isAdmin && (
            <div className="mb-3 rounded-2xl border border-copper/20 bg-copper/5 p-4 text-sm text-slate-700">
              Head to <Link to="/search" className="font-semibold text-copper hover:underline">Explore Stays</Link>, pick a property, and submit your request — your company admin will approve or decline.
            </div>
          )}
          {requests.length === 0 ? (
            <div className="rounded-3xl bg-white p-10 text-center text-slate-500">No booking requests yet.</div>
          ) : requests.map((r) => (
            <div key={r.id} data-testid={`request-${r.id}`} className="rounded-3xl border border-slate-200 bg-white p-5 card-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-lg font-bold text-slate-900">{r.property_title}</div>
                  <div className="mt-1 text-sm text-slate-600">
                    <b>{r.employee_name || r.employee_email}</b> · {new Date(r.check_in).toLocaleString()} · {r.duration_hours}h
                  </div>
                  {r.justification && <div className="mt-2 text-xs italic text-slate-600">&ldquo;{r.justification}&rdquo;</div>}
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-bold">{format(r.total_zmw)}</div>
                  <RequestStatusPill status={r.status} />
                  {r.status === "approved" && r.booking_id && (
                    <a
                      href={`${API}/bookings/${r.booking_id}/invoice.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`download-invoice-${r.booking_id}`}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-copper hover:underline"
                    >
                      <FileDown className="h-3 w-3" /> Invoice PDF
                    </a>
                  )}
                </div>
              </div>
              {isAdmin && r.status === "pending" && (
                deciding === r.id ? (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <input value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid={`decision-reason-${r.id}`}
                      placeholder="Optional note to the employee"
                      className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-copper focus:bg-white" />
                    <button onClick={() => decide(r, "approve")} data-testid={`approve-${r.id}`} className="btn-primary flex items-center gap-1 text-xs !py-2 !px-3">
                      <CheckCircle2 className="h-3 w-3" /> Approve
                    </button>
                    <button onClick={() => decide(r, "reject")} data-testid={`reject-${r.id}`} className="flex items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-rose-600 hover:border-rose-400">
                      <XCircle className="h-3 w-3" /> Decline
                    </button>
                    <button onClick={() => { setDeciding(null); setReason(""); }} className="text-xs text-slate-500 hover:text-slate-800">Cancel</button>
                  </div>
                ) : (
                  <div className="mt-4">
                    <button onClick={() => { setDeciding(r.id); setReason(""); }} data-testid={`decide-${r.id}`} className="btn-primary text-xs !py-2 !px-3">
                      Approve or decline
                    </button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "team" && isAdmin && (
        <div className="mt-6">
          <form onSubmit={invite} className="mb-6 rounded-3xl border border-slate-200 bg-white p-5 card-shadow" data-testid="invite-form">
            <div className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
              <UserPlus className="h-4 w-4 text-copper" /> Invite a teammate
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              <input data-testid="invite-email" type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@acme.co.zm"
                className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
              <input data-testid="invite-name" type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                placeholder="Name (optional)"
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
              <select data-testid="invite-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white">
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="mt-3 flex justify-end">
              <button type="submit" data-testid="invite-submit" disabled={inviting} className="btn-primary flex items-center gap-1 text-xs disabled:opacity-50">
                {inviting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Send invite
              </button>
            </div>
          </form>

          <BulkImportPanel onDone={softRefresh} />

          <div className="rounded-3xl border border-slate-200 bg-white card-shadow">
            <table className="w-full text-sm" data-testid="members-table">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {members.map((m) => (
                  <tr key={m.user_id} data-testid={`member-${m.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{m.name || "—"}</div>
                      <div className="text-xs text-slate-500">{m.email}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{m.role}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(m.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => remove(m)} data-testid={`remove-${m.email}`}
                        className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800">
                        <Trash2 className="h-3 w-3" /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`border-b-2 px-4 py-2 text-sm font-semibold transition ${active ? "border-copper text-copper" : "border-transparent text-slate-500 hover:text-slate-900"}`}
    >
      {children}
    </button>
  );
}

function RequestStatusPill({ status }) {
  const map = {
    pending: { label: "Pending approval", cls: "bg-amber-100 text-amber-800" },
    approved: { label: "Approved", cls: "bg-forest/10 text-forest" },
    rejected: { label: "Declined", cls: "bg-rose-100 text-rose-700" },
  };
  const it = map[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${it.cls}`}>{it.label}</span>;
}

function BulkImportPanel({ onDone }) {
  const [csvText, setCsvText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const readFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) {
      toast.error("File too large (max 500 KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(String(ev.target?.result || ""));
    reader.readAsText(f);
  };

  const downloadTemplate = () => {
    const sample = "email,name,role\nteammate@company.com,Full Name,employee\nfinance@company.com,Finance Lead,admin\n";
    const blob = new Blob([sample], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "easystay-team-invite-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    const trimmed = csvText.trim();
    if (!trimmed) return toast.error("Paste or upload a CSV first");
    try {
      setBusy(true);
      const { data } = await axios.post(`${API}/business/invites/bulk`, { csv_text: trimmed });
      setResult(data);
      const { summary } = data || {};
      toast.success(`Bulk import: ${summary?.added || 0} added, ${summary?.invited || 0} invited, ${summary?.skipped || 0} skipped`);
      if (onDone) await onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-5 card-shadow" data-testid="bulk-import-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <Upload className="h-4 w-4 text-copper" /> Bulk import teammates
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={downloadTemplate}
            data-testid="bulk-csv-template"
            className="flex items-center gap-1 text-xs font-semibold text-forest hover:underline"
          >
            <Download className="h-3 w-3" /> Download template
          </button>
          <label className="flex cursor-pointer items-center gap-1 text-xs font-semibold text-copper hover:underline">
            <FileText className="h-3 w-3" />
            <span>Upload CSV</span>
            <input type="file" accept=".csv,text/csv,text/plain" onChange={readFile} className="hidden" data-testid="bulk-csv-file" />
          </label>
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500">
        Header row required. Columns: <code className="rounded bg-slate-100 px-1">email</code>, <code className="rounded bg-slate-100 px-1">name</code> (optional), <code className="rounded bg-slate-100 px-1">role</code> (optional: <i>employee</i>/<i>admin</i>).
      </div>
      <textarea
        data-testid="bulk-csv-textarea"
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
        rows={5}
        placeholder={"email,name,role\nsam@acme.co.zm,Sam Mwansa,employee\nfin@acme.co.zm,Finance,admin"}
        className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-xs outline-none focus:border-copper focus:bg-white"
      />
      <div className="mt-3 flex items-center justify-between gap-3">
        {result && (
          <div className="text-xs text-slate-600" data-testid="bulk-result-summary">
            <b className="text-forest">{result.summary?.added || 0}</b> added ·{" "}
            <b className="text-copper">{result.summary?.invited || 0}</b> invited ·{" "}
            <b className="text-rose-600">{result.summary?.skipped || 0}</b> skipped
          </div>
        )}
        <button
          onClick={submit}
          disabled={busy || !csvText.trim()}
          data-testid="bulk-submit"
          className="btn-primary ml-auto flex items-center gap-1 text-xs disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Import all
        </button>
      </div>
      {result?.results?.length > 0 && (
        <div className="mt-4 max-h-56 overflow-auto rounded-xl border border-slate-100 bg-slate-50" data-testid="bulk-result-list">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-slate-100">
              {result.results.map((r, i) => (
                <tr key={`${r.email}-${i}`}>
                  <td className="px-3 py-2 font-medium text-slate-700">{r.email}</td>
                  <td className="px-3 py-2 capitalize">{r.role || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`inline-block rounded-full px-2 py-0.5 font-semibold ${
                      r.status === "added" ? "bg-forest/10 text-forest" :
                      r.status === "invited" ? "bg-copper/10 text-copper" :
                      "bg-rose-100 text-rose-700"
                    }`}>{r.status}</span>
                    {r.reason && <div className="mt-0.5 text-[10px] text-slate-500">{r.reason}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
